// Frontend API caller for /api/auth — wallet sign-in for business owners and waiters (task 175).
// Connecting a wallet only looks up its handle (public). An auth session needs a signed sign-in message:
// requestSignInMessage → wallet signs → verifySignIn. Payers never sign in.

import type { Result } from '@shared/types/result.ts';
import { ok, err } from '@shared/types/result.ts';
import type { WalletLookupResult } from '@shared/types/wallet.ts';

export type SignInResponse = Readonly<{
  readonly sessionId: string;
  readonly lookup: WalletLookupResult;
}>;

export type StoredSessionCheck = Readonly<{
  readonly walletAddress: string;
  readonly lookup: WalletLookupResult;
}>;

const _call = async <T>(path: string, init: RequestInit = {}): Promise<Result<T>> => {
  try {
    const response = await fetch(`/api/auth${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
    const body = await response.json().catch(() => ({ error: response.statusText }));
    if (!response.ok) return err(typeof body.error === 'string' ? body.error : `Request failed (${response.status})`);
    return ok(body.data as T);
  } catch (e) {
    return err(e instanceof Error ? e.message : 'Network error');
  }
};

const _bearer = (sessionId: string): Record<string, string> => ({ Authorization: `Bearer ${sessionId}` });

// Public: the handle (if any) this address owns
export const lookupAddress = async (address: string): Promise<Result<WalletLookupResult>> => {
  const result = await _call<{ lookup: WalletLookupResult }>(`/lookup/${encodeURIComponent(address)}`);
  return result.ok ? ok(result.value.lookup) : result;
};

// The message the wallet must sign (single use, valid 5 minutes)
export const requestSignInMessage = async (walletAddress: string, chainId: number): Promise<Result<string>> => {
  const result = await _call<{ message: string }>('/challenge', {
    method: 'POST',
    body: JSON.stringify({ walletAddress, chainId }),
  });
  return result.ok ? ok(result.value.message) : result;
};

export const verifySignIn = (message: string, signature: string): Promise<Result<SignInResponse>> =>
  _call<SignInResponse>('/verify', { method: 'POST', body: JSON.stringify({ message, signature }) });

// Is a session saved from an earlier sign-in still valid? (error = expired/unknown → sign in again)
export const checkStoredSession = (sessionId: string): Promise<Result<StoredSessionCheck>> =>
  _call<StoredSessionCheck>('/session', { headers: _bearer(sessionId) });

export const signOutSession = (sessionId: string): Promise<Result<{ signedOut: boolean }>> =>
  _call<{ signedOut: boolean }>('/sign-out', { method: 'POST', headers: _bearer(sessionId) });

// Local dev mode only (the route exists only with .dev.env): a dev identity can't sign, the server vouches
export const devSignIn = async (key: string): Promise<Result<SignInResponse>> => {
  try {
    const response = await fetch('/api/dev/sign-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key }),
    });
    const body = await response.json().catch(() => ({ error: response.statusText }));
    return response.ok ? ok(body.data as SignInResponse) : err(body.error ?? 'Dev sign-in failed');
  } catch (e) {
    return err(e instanceof Error ? e.message : 'Network error');
  }
};
